import { fetchPrivateFileAccess } from '@/api';

export const ATTACHMENT_CRYPTO_ERROR_MESSAGE =
  'Attachment downloads require HTTPS or localhost and a browser with Web Crypto support.';

/** Encode record IDs as single URL segments and reject absent context. */
function segment(value) {
  if (typeof value !== 'string' || !value) {
    throw new Error('The file is missing its record context.');
  }

  return encodeURIComponent(value);
}

/** Produce the same opaque attachment identifier used by the server's saved-answer lookup. */
export async function attachmentId(content) {
  if (typeof crypto === 'undefined' || typeof crypto?.subtle?.digest !== 'function') {
    const error = new Error(ATTACHMENT_CRYPTO_ERROR_MESSAGE);
    error.code = 'attachment-crypto-unavailable';

    throw error;
  }

  const bytes = new TextEncoder().encode(content);
  const digest = await crypto.subtle.digest('SHA-256', bytes);

  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}

/** Accept only the HTTPS Storage URLs and expiration metadata issued by our file endpoint. */
async function fileAccess(path, options) {
  const access = await fetchPrivateFileAccess(path, options);
  const url = new URL(access?.url);

  if (url.protocol !== 'https:' || url.hostname !== 'storage.googleapis.com'
      || url.username || url.password || !Number.isFinite(Date.parse(access?.expiresAt))) {
    throw new Error('File access is unavailable. Please try again.');
  }

  return access;
}

/** Retrieve an attachment by license and digest; never send a Storage URL to the API. */
export async function licenseAttachmentAccess({ cityId, licenseId, content }, { download = false, signal } = {}) {
  const id = await attachmentId(content);
  const path = `/v1/cities/${segment(cityId)}/licenses/${segment(licenseId)}/attachments/${id}`;

  return fileAccess(`${path}${download ? '?download=1' : ''}`, { signal });
}

/** Fetch the screenshot selected by the listing's current persisted evidence record. */
export async function listingEvidenceAccess(cityId, listingId, options = {}) {
  const path = `/v1/cities/${segment(cityId)}/listings/${segment(listingId)}/evidence/file`;

  return fileAccess(path, options);
}
